// AdminPanel.jsx
// Admin: Login → Requests (approve / reject / EDIT-AFTER-APPROVE / RESEND) → Stats
//        → 📭 No Email (Reg No. se aaye students jinka email DB me nahi)
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import CertificatePreviewModal from "./CertificatePreviewModal";
import DocumentsPanel from "./DocumentsPanel";
import CertificatesPanel from "./CertificatesPanel";
import MissingEmailPanel from "./MissingEmailPanel";   // 📭 NEW

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const TABS = {
  REQUESTS:      "requests",
  MISSING_EMAIL: "missingEmail",   // 📭 NEW
  DOWNLOADS:     "downloads",
  WEBINAR:       "webinar",
  DOCUMENTS:     "documents",
  CERTIFICATES:  "certificates",
};

const COURSE_DURATIONS = {
  "Data Science": "6-7",
  "Data Analytics With AI": "7",
  "Data Analytics": "5",
  "Business Analyst": "5",
  "Artificial Intelligence": "8-9",
  "Data Science With AI": "9",
  "Playwright Automation With AI": "3",
  "Python Playwright Automation With AI": "3",
  "API + Automation With AI": "3",
  "Java Full Stack": "6-7",
  "Python Full Stack": "6-7",
  "Mern Full Stack": "5",
  "Software Testing": "5",
  "API + Automation": "3",
  "Web Automation": "2.5",
  "AWS": "3",
  "DevOps": "3",
  "AWS+DevOps": "6",
  "Digital Marketing": "4",
  "Adv Digital Marketing": "6",
  "Ethical Hacking": "2.5",
  "Cyber Security": "5",
  "UI/UX": "3",
  "Graphics Design": "3",
  "Video Editing": "4",
  "Java with DSA": "2",
  "C with DSA": "2",
  "C & C++": "2",
};

/* ── link kitna bacha hai (2 din wala) ── */
function linkStatus(req) {
  if (!req.tokenExpiresAt) return { live: false, label: "No link" };
  const ms = new Date(req.tokenExpiresAt).getTime() - Date.now();
  if (ms <= 0) return { live: false, label: "Expired" };
  const h = Math.floor(ms / 3600000);
  const d = Math.floor(h / 24);
  return {
    live: true,
    label: d >= 1 ? `Valid ${d}d ${h % 24}h` : `Valid ${h}h ${Math.floor((ms % 3600000) / 60000)}m`,
  };
}

export default function AdminPanel() {
  const [adminKey, setAdminKey] = useState("");
  const [inputKey, setInputKey] = useState("");
  const [authError, setAuthError] = useState("");
  const [activeTab, setActiveTab] = useState(TABS.REQUESTS);
  const [filter, setFilter]       = useState("pending");
  const [previewCert, setPreviewCert] = useState(null);
  const [requests, setRequests]   = useState([]);
  const [requestSearch, setRequestSearch] = useState("");
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [csvPreview, setCsvPreview] = useState(null);
  const [confirmSending, setConfirmSending] = useState(false);
  const [busyId, setBusyId] = useState("");

  // 📭 tab par badge — kitne students ka email abhi bhi missing hai
  const [missingCount, setMissingCount] = useState(0);

  const [editingReq, setEditingReq] = useState(null);
  // ✅ email + phone bhi editable, aur "resend" option
  const [editForm, setEditForm] = useState({
    name: "", email: "", phone: "", registrationNo: "",
    course: "", months: "", completionDate: "", adminRemark: "",
    isCustomCourse: false, resend: false,
  });

  // ── Webinar state ──
  const [webinarStudents, setWebinarStudents] = useState([]);
  const [webinarSearch, setWebinarSearch]     = useState("");
  const [webinarLoading, setWebinarLoading]   = useState(false);
  const [csvUploading, setCsvUploading]       = useState(false);
  const [csvMsg, setCsvMsg]                   = useState("");
  const [editingWebinar, setEditingWebinar]   = useState(null);
  const [webinarPage, setWebinarPage]         = useState(1);
  const [webinarEditForm, setWebinarEditForm] = useState({
    Name: "", email: "", Course_Name: "", Date: "", Duration: "",
  });

  const fileInputRef = useRef(null);
  const isLoggedIn = !!adminKey;

  const filteredRequests = requests.filter((req) => {
    if (!requestSearch.trim()) return true;
    const q = requestSearch.trim().toLowerCase();
    return (
      (req.name  && req.name.toLowerCase().includes(q)) ||
      (req.email && req.email.toLowerCase().includes(q)) ||
      (req.registrationNo && req.registrationNo.toLowerCase().includes(q)) ||
      (req.phone && req.phone.includes(q))
    );
  });

  // ── Login ──
  async function handleLogin(e) {
    e.preventDefault();
    setAuthError(""); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/requests?status=pending`, {
        headers: { "x-admin-key": inputKey },
      });
      if (res.status === 401) setAuthError("❌ Wrong admin key. Dobara try karein.");
      else setAdminKey(inputKey);
    } catch {
      setAuthError("Server se connect nahi ho pa raha.");
    } finally { setLoading(false); }
  }

  async function fetchRequests() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/requests?status=${filter}`, {
        headers: { "x-admin-key": adminKey },
      });
      const data = await res.json();
      setRequests(data.requests || []);
    } catch { setRequests([]); }
    finally { setLoading(false); }
  }

  // 📭 sirf count chahiye — badge ke liye
  async function fetchMissingCount() {
    try {
      const res = await fetch(`${API_BASE}/admin/missing-email?status=pending`, {
        headers: { "x-admin-key": adminKey },
      });
      const data = await res.json();
      setMissingCount(data.counts?.pending || 0);
    } catch { /* badge hi hai, ignore */ }
  }

  async function fetchWebinarStudents(searchEmail = "") {
    setWebinarPage(1); setWebinarLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/admin/webinar-students?email=${encodeURIComponent(searchEmail)}`,
        { headers: { "x-admin-key": adminKey } }
      );
      const data = await res.json();
      setWebinarStudents(data.students || []);
    } catch { setWebinarStudents([]); }
    finally { setWebinarLoading(false); }
  }

  async function fetchDownloads() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/downloads`, {
        headers: { "x-admin-key": adminKey },
      });
      const data = await res.json();
      setDownloads(data.downloads || []);
    } catch { setDownloads([]); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    if (!isLoggedIn) return;
    if (activeTab === TABS.REQUESTS)  fetchRequests();
    if (activeTab === TABS.DOWNLOADS) fetchDownloads();
    if (activeTab === TABS.WEBINAR)   fetchWebinarStudents();
    fetchMissingCount();   // 📭 har tab change par badge refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, activeTab, filter]);

  useEffect(() => { setRequestSearch(""); }, [filter]);

  // ── Approve (PDF banega + mail jayega, thoda time lagta hai) ──
  async function handleApprove(id) {
    setActionMsg(""); setBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/admin/approve/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-key": adminKey },
      });
      const data = await res.json();
      setActionMsg(data.message || "Approved!");
      fetchRequests();
    } catch {
      setActionMsg("Action complete nahi hua. Dobara try karein.");
    } finally { setBusyId(""); }
  }

  // ── ✅ Resend — approve ke baad dobara certificate bhejo ──
  async function handleResend(req) {
    const newEmail = window.prompt(
      "Certificate kis email par bhejna hai?\n(change karna ho to edit karein, warna OK dabayein)",
      req.email || ""
    );
    if (newEmail === null) return;

    setActionMsg(""); setBusyId(req._id);
    try {
      const res = await fetch(`${API_BASE}/admin/resend/${req._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-key": adminKey },
        body: JSON.stringify({ email: newEmail.trim(), resetDownloads: false }),
      });
      const data = await res.json();
      setActionMsg(data.message || (data.success ? "Certificate resent!" : "Resend failed"));
      fetchRequests();
    } catch {
      setActionMsg("Resend nahi hua. Dobara try karein.");
    } finally { setBusyId(""); }
  }

  // ── Reject ──
  async function handleReject(id) {
    const reason = window.prompt("Rejection reason (optional):");
    if (reason === null) return;
    try {
      const res = await fetch(`${API_BASE}/admin/reject/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-key": adminKey },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      setActionMsg(data.message || "Rejected.");
      fetchRequests();
    } catch { setActionMsg("Reject nahi hua. Dobara try karein."); }
  }

  // ── Edit modal (ab approved par bhi khulta hai) ──
  function openEditModal(req) {
    setEditingReq(req);
    const courseVal = req.course || "";
    const isKnownCourse = Object.keys(COURSE_DURATIONS).includes(courseVal);
    setEditForm({
      name:           req.name || "",
      email:          req.email || "",
      phone:          req.phone || "",
      registrationNo: req.registrationNo || "",
      course:         courseVal,
      months:         req.months || "",
      completionDate: req.completionDate || "",
      adminRemark:    req.adminRemark || "",
      isCustomCourse: courseVal !== "" && !isKnownCourse,
      resend:         req.status === "approved",   // approved hai to default ON
    });
  }
  function closeEditModal() { setEditingReq(null); }

  function handleCourseChange(course) {
    setEditForm((prev) => ({
      ...prev, course, isCustomCourse: false,
      months: COURSE_DURATIONS[course] || prev.months,
    }));
  }

  async function handleSaveEdit() {
    setBusyId(editingReq._id);
    try {
      const res = await fetch(`${API_BASE}/admin/update/${editingReq._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "x-admin-key": adminKey },
        body: JSON.stringify({
          name:           editForm.name,
          email:          editForm.email,
          phone:          editForm.phone,
          registrationNo: editForm.registrationNo,
          course:         editForm.course,
          months:         editForm.months,
          completionDate: editForm.completionDate,
          adminRemark:    editForm.adminRemark,
          resend:         editForm.resend,   // ✅ save + naya certificate mail
        }),
      });
      const data = await res.json();
      setActionMsg(data.message || "Updated!");
      closeEditModal();
      fetchRequests();
    } catch {
      setActionMsg("Update nahi hua. Dobara try karein.");
    } finally { setBusyId(""); }
  }

  // ── CSV (webinar) ──
  async function handleCsvUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setCsvMsg(""); setCsvUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_BASE}/profile/preview`, formData, {
        headers: { "Content-Type": "multipart/form-data", "x-admin-key": adminKey },
      });
      setCsvPreview({ total: res.data.total, data: res.data.data });
    } catch (err) {
      setCsvMsg(`❌ Upload failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setCsvUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleConfirmCsv() {
    if (!csvPreview) return;
    setConfirmSending(true);
    try {
      const res = await axios.post(
        `${API_BASE}/profile/confirm`,
        { data: csvPreview.data },
        { headers: { "Content-Type": "application/json", "x-admin-key": adminKey } }
      );
      setCsvMsg(`✅ ${res.data.total} students upload ho gaye!`);
      setCsvPreview(null);
      fetchWebinarStudents();
    } catch (err) {
      setCsvMsg(`❌ Confirm failed: ${err.response?.data?.message || err.message}`);
    } finally { setConfirmSending(false); }
  }

  function openWebinarEdit(student) {
    setEditingWebinar(student);
    setWebinarEditForm({
      Name: student.Name || "", email: student.email || "",
      Course_Name: student.Course_Name || "", Date: student.Date || "",
      Duration: student.Duration || "",
    });
  }
  function closeWebinarEdit() { setEditingWebinar(null); }

  async function handleSaveWebinarEdit() {
    try {
      const res = await fetch(`${API_BASE}/admin/webinar-student/${editingWebinar._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "x-admin-key": adminKey },
        body: JSON.stringify(webinarEditForm),
      });
      const data = await res.json();
      setActionMsg(data.message || "Updated!");
      closeWebinarEdit();
      fetchWebinarStudents(webinarSearch);
    } catch { setActionMsg("Update nahi hua."); }
  }

  async function handleDeleteWebinar(id) {
    if (!window.confirm("Is student ko delete karna chahte ho?")) return;
    try {
      const res = await fetch(`${API_BASE}/admin/webinar-student/${id}`, {
        method: "DELETE", headers: { "x-admin-key": adminKey },
      });
      const data = await res.json();
      setActionMsg(data.message || "Deleted!");
      fetchWebinarStudents(webinarSearch);
    } catch { setActionMsg("Delete nahi hua."); }
  }

  // ── Login screen ──
  if (!isLoggedIn) {
    return (
      <div style={styles.page}>
        <div style={styles.loginCard}>
          <div style={styles.logo}>🔐 Admin Panel</div>
          <p style={styles.subtitle}>Uncodemy Certificate Portal</p>
          <form onSubmit={handleLogin}>
            <input style={styles.input} type="password" placeholder="Admin Secret Key"
              value={inputKey} onChange={(e) => setInputKey(e.target.value)}
              required disabled={loading} />
            {authError && <div style={styles.errorBox}>{authError}</div>}
            <button style={styles.btnPrimary} type="submit" disabled={loading}>
              {loading ? "Checking..." : "Login →"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard ──
  return (
    <div style={styles.dashPage}>
      <div style={styles.topbar}>
        <div style={styles.topbarLogo}>🎓 Uncodemy Admin</div>
        <button style={styles.btnLogout} onClick={() => setAdminKey("")}>Logout</button>
      </div>

      <div style={styles.dashBody}>
        {actionMsg && (
          <div style={styles.successBanner}>
            ✅ {actionMsg}
            <button style={styles.closeBanner} onClick={() => setActionMsg("")}>×</button>
          </div>
        )}

        <div style={styles.tabRow}>
          <TabBtn active={activeTab === TABS.REQUESTS} onClick={() => setActiveTab(TABS.REQUESTS)}>📋 Certificate Requests</TabBtn>
          {/* 📭 NEW */}
          <TabBtn active={activeTab === TABS.MISSING_EMAIL} onClick={() => setActiveTab(TABS.MISSING_EMAIL)}>
            📭 No Email (Reg No.)
            {missingCount > 0 && (
              <span style={{
                ...styles.tabBadge,
                ...(activeTab === TABS.MISSING_EMAIL ? styles.tabBadgeActive : {}),
              }}>{missingCount}</span>
            )}
          </TabBtn>
          <TabBtn active={activeTab === TABS.DOWNLOADS} onClick={() => setActiveTab(TABS.DOWNLOADS)}>📊 Download Stats</TabBtn>
          <TabBtn active={activeTab === TABS.WEBINAR} onClick={() => setActiveTab(TABS.WEBINAR)}>🎥 Webinar Students</TabBtn>
          <TabBtn active={activeTab === TABS.DOCUMENTS} onClick={() => setActiveTab(TABS.DOCUMENTS)}>📄 HR Documents</TabBtn>
          <TabBtn active={activeTab === TABS.CERTIFICATES} onClick={() => setActiveTab(TABS.CERTIFICATES)}>🎓 Certificates</TabBtn>
        </div>

        {/* ── Requests Tab ── */}
        {activeTab === TABS.REQUESTS && (
          <div>
            <div style={styles.filterRow}>
              {["pending", "approved", "rejected", "all"].map((f) => (
                <button key={f}
                  style={{ ...styles.filterBtn, ...(filter === f ? styles.filterBtnActive : {}) }}
                  onClick={() => setFilter(f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              <button style={styles.refreshBtn} onClick={fetchRequests}>🔄 Refresh</button>
            </div>

            <div style={styles.reqSearchBox}>
              <input style={{ ...styles.input, marginBottom: 0, flex: 1 }} type="text"
                placeholder="🔍 Naam / Email / Registration No. se search karein..."
                value={requestSearch} onChange={(e) => setRequestSearch(e.target.value)} />
              {requestSearch && (
                <button style={{ ...styles.btnSearch, background: "#f1f5f9", color: "#475569" }}
                  onClick={() => setRequestSearch("")}>Clear</button>
              )}
            </div>

            {loading ? (
              <div style={styles.centered}>⏳ Loading...</div>
            ) : filteredRequests.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>📭</div>
                <p>{requestSearch ? "Is search se koi request nahi mili." : `Koi ${filter} requests nahi hain.`}</p>
              </div>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {["Naam", "Email / Phone", "Reg. No.", "Course", "Duration", "Date", "Remark", "Status", "Cert Link", "Actions"].map((h) => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map((req) => {
                      const ls = linkStatus(req);
                      const busy = busyId === req._id;
                      return (
                        <tr key={req._id} style={styles.tr}>
                          <td style={styles.td}>{req.name}</td>
                          <td style={styles.td}>
                            {req.email}
                            {req.phone ? <><br /><span style={{ fontSize: 12, color: "#64748b" }}>📞 {req.phone}</span></> : null}
                          </td>
                          <td style={styles.td}>
                            {req.registrationNo ? <span style={styles.verifyBadge}>{req.registrationNo}</span> : "—"}
                          </td>
                          <td style={styles.td}>{req.course || "—"}</td>
                          <td style={styles.td}>{req.months ? `${req.months}m` : "—"}</td>
                          <td style={styles.td}>{formatDate(req.requestedAt)}</td>
                          <td style={styles.td}>{req.remark || "—"}</td>
                          <td style={styles.td}><StatusBadge status={req.status} /></td>
                          <td style={styles.td}>
                            {req.status === "approved" ? (
                              <span style={{
                                ...styles.linkBadge,
                                background: ls.live ? "#dcfce7" : "#fee2e2",
                                color:      ls.live ? "#166534" : "#991b1b",
                              }}>
                                {ls.live ? "🟢" : "🔴"} {ls.label}
                              </span>
                            ) : "—"}
                            {req.resendCount > 0 && (
                              <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                                🔁 resent {req.resendCount}x
                              </div>
                            )}
                          </td>
                          <td style={styles.td}>
                            {req.status === "pending" && (
                              <div style={styles.actionRow}>
                                <button style={styles.btnEdit} onClick={() => openEditModal(req)}>✏️ Edit</button>
                                <button style={styles.btnApprove} disabled={busy} onClick={() => handleApprove(req._id)}>
                                  {busy ? "Sending..." : "✅ Approve"}
                                </button>
                                <button style={styles.btnReject} onClick={() => handleReject(req._id)}>❌ Reject</button>
                              </div>
                            )}

                            {/* ✅ APPROVED — ab bhi edit + resend */}
                            {req.status === "approved" && (
                              <div style={styles.actionRow}>
                                <button style={styles.btnEdit} onClick={() => openEditModal(req)}>✏️ Edit</button>
                                <button style={styles.btnResend} disabled={busy} onClick={() => handleResend(req)}>
                                  {busy ? "Sending..." : "🔁 Resend"}
                                </button>
                                <button style={styles.btnView} onClick={() => setPreviewCert(req)}>👁️ View</button>
                                <span style={{ fontSize: 11, color: "#64748b", width: "100%" }}>
                                  📤 {req.lastSentAt ? formatDate(req.lastSentAt) : "—"} ·{" "}
                                  {req.downloadCount > 0 ? `📥 ${req.downloadCount}x downloaded` : "not downloaded"}
                                </span>
                              </div>
                            )}

                            {req.status === "rejected" && (
                              <div style={styles.actionRow}>
                                <span style={{ fontSize: 12, color: "#dc2626" }}>
                                  {req.rejectionReason || "No reason given"}
                                </span>
                                <button style={styles.btnEdit} onClick={() => openEditModal(req)}>✏️ Edit</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── 📭 No Email Tab ── */}
        {activeTab === TABS.MISSING_EMAIL && <MissingEmailPanel adminKey={adminKey} />}

        {/* ── Downloads Tab ── */}
        {activeTab === TABS.DOWNLOADS && (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
              <button style={styles.refreshBtn} onClick={fetchDownloads}>🔄 Refresh</button>
            </div>
            {loading ? (
              <div style={styles.centered}>⏳ Loading...</div>
            ) : downloads.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>📊</div>
                <p>Abhi koi download nahi hua.</p>
              </div>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>{["Email", "Download Time", "Re-attempts", "Last Attempt"].map((h) => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {downloads.map((d) => (
                      <tr key={d._id} style={styles.tr}>
                        <td style={styles.td}>{d.email}</td>
                        <td style={styles.td}>{formatDate(d.usedAt)}</td>
                        <td style={styles.td}>
                          {d.reAttemptCount > 0
                            ? <span style={styles.reattemptBadge}>⚠️ {d.reAttemptCount}x</span>
                            : <span style={{ color: "#059669" }}>0</span>}
                        </td>
                        <td style={styles.td}>{d.lastAttemptAt ? formatDate(d.lastAttemptAt) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === TABS.DOCUMENTS    && <DocumentsPanel adminKey={adminKey} />}
        {activeTab === TABS.CERTIFICATES && <CertificatesPanel adminKey={adminKey} />}

        {/* ── Webinar Tab ── */}
        {activeTab === TABS.WEBINAR && (
          <div>
            <div style={styles.webinarTopRow}>
              <div style={styles.uploadBox}>
                <div style={styles.uploadTitle}>📤 CSV Upload</div>
                <p style={styles.uploadHint}>Columns: <code>Name, email, Course_Name, Date, Duration</code></p>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvUpload}
                    style={{ display: "none" }} id="csvInput" />
                  <label htmlFor="csvInput" style={styles.uploadBtn}>
                    {csvUploading ? "Uploading..." : "📂 Choose CSV"}
                  </label>
                  {csvMsg && (
                    <span style={{ fontSize: 13, fontWeight: 600, color: csvMsg.startsWith("✅") ? "#065f46" : "#dc2626" }}>
                      {csvMsg}
                    </span>
                  )}
                </div>
              </div>

              <div style={styles.searchBox}>
                <div style={styles.uploadTitle}>🔍 Search by Email</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input style={{ ...styles.input, marginBottom: 0, flex: 1 }} type="email"
                    placeholder="student@email.com" value={webinarSearch}
                    onChange={(e) => setWebinarSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { setWebinarPage(1); fetchWebinarStudents(webinarSearch); } }} />
                  <button style={styles.btnSearch}
                    onClick={() => { setWebinarPage(1); fetchWebinarStudents(webinarSearch); }}>Search</button>
                  {webinarSearch && (
                    <button style={{ ...styles.btnSearch, background: "#f1f5f9", color: "#475569" }}
                      onClick={() => { setWebinarSearch(""); setWebinarPage(1); fetchWebinarStudents(""); }}>Clear</button>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.statsRow}>
              <div style={styles.statCard}>
                <div style={styles.statNum}>{webinarStudents.length}</div>
                <div style={styles.statLabel}>{webinarSearch ? "Results Found" : "Total Students"}</div>
              </div>
            </div>

            {webinarLoading ? (
              <div style={styles.centered}>⏳ Loading...</div>
            ) : webinarStudents.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>🎥</div>
                <p>{webinarSearch ? "Koi student is email se nahi mila." : "Koi webinar student nahi hai. CSV upload karo."}</p>
              </div>
            ) : (() => {
              const PAGE_SIZE = 15;
              const totalPages = Math.ceil(webinarStudents.length / PAGE_SIZE);
              const pageStudents = webinarStudents.slice((webinarPage - 1) * PAGE_SIZE, webinarPage * PAGE_SIZE);

              return (
                <div>
                  <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                      <thead>
                        <tr>{["#", "Name", "Email", "Course", "Date", "Duration", "Verify ID", "Actions"].map((h) => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {pageStudents.map((s, i) => (
                          <tr key={s._id} style={styles.tr}>
                            <td style={{ ...styles.td, color: "#94a3b8", fontSize: 12 }}>
                              {(webinarPage - 1) * PAGE_SIZE + i + 1}
                            </td>
                            <td style={styles.td}><b>{s.Name}</b></td>
                            <td style={styles.td}>{s.email}</td>
                            <td style={styles.td}>{s.Course_Name}</td>
                            <td style={styles.td}>{s.Date}</td>
                            <td style={styles.td}>{s.Duration}</td>
                            <td style={styles.td}><span style={styles.verifyBadge}>{s.verifyId || "—"}</span></td>
                            <td style={styles.td}>
                              <div style={{ display: "flex", gap: 8 }}>
                                <button style={styles.btnEdit} onClick={() => openWebinarEdit(s)}>✏️ Edit</button>
                                <button style={styles.btnReject} onClick={() => handleDeleteWebinar(s._id)}>🗑 Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div style={styles.pagination}>
                      <button style={{ ...styles.pageBtn, ...(webinarPage === 1 ? styles.pageBtnDisabled : {}) }}
                        onClick={() => setWebinarPage((p) => Math.max(1, p - 1))} disabled={webinarPage === 1}>← Prev</button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((p) => p === 1 || p === totalPages || (p >= webinarPage - 2 && p <= webinarPage + 2))
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                          acc.push(p); return acc;
                        }, [])
                        .map((p, idx) => p === "..." ? (
                          <span key={`dots-${idx}`} style={styles.pageDots}>…</span>
                        ) : (
                          <button key={p} style={{ ...styles.pageBtn, ...(webinarPage === p ? styles.pageBtnActive : {}) }}
                            onClick={() => setWebinarPage(p)}>{p}</button>
                        ))}
                      <button style={{ ...styles.pageBtn, ...(webinarPage === totalPages ? styles.pageBtnDisabled : {}) }}
                        onClick={() => setWebinarPage((p) => Math.min(totalPages, p + 1))} disabled={webinarPage === totalPages}>Next →</button>
                      <span style={styles.pageInfo}>
                        {(webinarPage - 1) * PAGE_SIZE + 1}–{Math.min(webinarPage * PAGE_SIZE, webinarStudents.length)} of {webinarStudents.length}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* ── Edit Request Modal ── */}
      {editingReq && (
        <div style={styles.modalOverlay} onClick={closeEditModal}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              ✏️ Edit Request — {editingReq.name} <StatusBadge status={editingReq.status} />
            </h3>

            <label style={styles.modalLabel}>Name</label>
            <input style={styles.input} placeholder="Student ka naam..."
              value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />

            <label style={styles.modalLabel}>Email (certificate isi par jayega)</label>
            <input style={styles.input} type="email" placeholder="student@example.com"
              value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />

            <label style={styles.modalLabel}>Phone</label>
            <input style={styles.input} placeholder="9876543210"
              value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />

            <label style={styles.modalLabel}>Registration No.</label>
            <input style={styles.input} placeholder="UC2024/BA-Poo/01-02"
              value={editForm.registrationNo}
              onChange={(e) => setEditForm({ ...editForm, registrationNo: e.target.value })} />

            <label style={styles.modalLabel}>Course</label>
            <select style={styles.input}
              value={editForm.isCustomCourse ? "__OTHER__"
                : Object.keys(COURSE_DURATIONS).includes(editForm.course) ? editForm.course : ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "__OTHER__") setEditForm({ ...editForm, course: "", isCustomCourse: true });
                else handleCourseChange(val);
              }}>
              <option value="">-- Select Course --</option>
              {Object.keys(COURSE_DURATIONS).map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__OTHER__">✏️ Other (Type manually)</option>
            </select>

            {editForm.isCustomCourse && (
              <input style={{ ...styles.input, marginTop: 6 }} placeholder="Type course name manually..."
                value={editForm.course}
                onChange={(e) => setEditForm({ ...editForm, course: e.target.value })} autoFocus />
            )}

            <label style={styles.modalLabel}>Duration (months)</label>
            <input style={styles.input} value={editForm.months}
              onChange={(e) => setEditForm({ ...editForm, months: e.target.value })} />

            <label style={styles.modalLabel}>Completion Date</label>
            <input type="date" style={styles.input} value={editForm.completionDate}
              onChange={(e) => setEditForm({ ...editForm, completionDate: e.target.value })} />

            <label style={styles.modalLabel}>Admin Remark</label>
            <textarea style={{ ...styles.input, minHeight: 70, resize: "vertical" }}
              placeholder="Optional internal note..."
              value={editForm.adminRemark}
              onChange={(e) => setEditForm({ ...editForm, adminRemark: e.target.value })} />

            {/* ✅ approved request — save karke naya certificate bhej do */}
            {editingReq.status === "approved" && (
              <label style={styles.checkRow}>
                <input type="checkbox" checked={editForm.resend}
                  onChange={(e) => setEditForm({ ...editForm, resend: e.target.checked })} />
                <span>
                  <b>Save karke naya certificate mail karein</b>
                  <br />
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    Naya PDF banega, purana link invalid ho jayega. Naya link 2 din valid.
                  </span>
                </span>
              </label>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button style={styles.btnCancel} onClick={closeEditModal}>Cancel</button>
              <button style={styles.btnSave} onClick={handleSaveEdit} disabled={!!busyId}>
                {busyId ? "Saving..." : editForm.resend ? "💾 Save & Send" : "💾 Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewCert && (
        <CertificatePreviewModal cert={previewCert} onClose={() => setPreviewCert(null)} />
      )}

      {/* ── Webinar Edit Modal ── */}
      {editingWebinar && (
        <div style={styles.modalOverlay} onClick={closeWebinarEdit}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>✏️ Edit Webinar Student — {editingWebinar.Name}</h3>
            <label style={styles.modalLabel}>Name</label>
            <input style={styles.input} value={webinarEditForm.Name}
              onChange={(e) => setWebinarEditForm({ ...webinarEditForm, Name: e.target.value })} />
            <label style={styles.modalLabel}>Email</label>
            <input style={styles.input} value={webinarEditForm.email}
              onChange={(e) => setWebinarEditForm({ ...webinarEditForm, email: e.target.value })} />
            <label style={styles.modalLabel}>Course Name</label>
            <input style={styles.input} value={webinarEditForm.Course_Name}
              onChange={(e) => setWebinarEditForm({ ...webinarEditForm, Course_Name: e.target.value })} />
            <label style={styles.modalLabel}>Date</label>
            <input style={styles.input} value={webinarEditForm.Date}
              onChange={(e) => setWebinarEditForm({ ...webinarEditForm, Date: e.target.value })} />
            <label style={styles.modalLabel}>Duration</label>
            <input style={styles.input} value={webinarEditForm.Duration}
              onChange={(e) => setWebinarEditForm({ ...webinarEditForm, Duration: e.target.value })} />
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button style={styles.btnCancel} onClick={closeWebinarEdit}>Cancel</button>
              <button style={styles.btnSave} onClick={handleSaveWebinarEdit}>💾 Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV Preview Modal ── */}
      {csvPreview && (
        <div style={styles.modalOverlay} onClick={() => setCsvPreview(null)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>📤 Confirm Upload — {csvPreview.total} students</h3>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
              Confirm karne ke baad DB me insert hoga aur sabko email jayega.
            </p>
            <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
              <table style={styles.table}>
                <thead>
                  <tr>{["Name", "email", "Course_Name", "Date", "Duration"].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {csvPreview.data.slice(0, 10).map((s, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{s.Name}</td>
                      <td style={styles.td}>{s.email}</td>
                      <td style={styles.td}>{s.Course_Name}</td>
                      <td style={styles.td}>{s.Date}</td>
                      <td style={styles.td}>{s.Duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {csvPreview.total > 10 && (
              <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>...aur {csvPreview.total - 10} students</p>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button style={styles.btnCancel} onClick={() => setCsvPreview(null)}>Cancel</button>
              <button style={styles.btnSave} onClick={handleConfirmCsv} disabled={confirmSending}>
                {confirmSending ? "Sending..." : "✅ Confirm & Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sub-components ── */

function TabBtn({ active, onClick, children }) {
  return (
    <button style={{ ...styles.tabBtn, ...(active ? styles.tabBtnActive : {}) }} onClick={onClick}>
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending:  { bg: "#fef9c3", color: "#854d0e", label: "⏳ Pending" },
    approved: { bg: "#d1fae5", color: "#065f46", label: "✅ Approved" },
    rejected: { bg: "#fee2e2", color: "#991b1b", label: "❌ Rejected" },
  };
  const c = config[status] || { bg: "#f1f5f9", color: "#475569", label: status };
  return (
    <span style={{ background: c.bg, color: c.color, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
      {c.label}
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return dateStr; }
}

/* ── Styles ── */

const styles = {
  page: { minHeight: "100vh", background: "#f0f4ff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Segoe UI', system-ui, sans-serif" },
  loginCard: { background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.10)", padding: "40px 44px", width: 360, textAlign: "center" },
  logo: { fontSize: 26, fontWeight: 700, color: "#ff5421", marginBottom: 4 },
  subtitle: { color: "#64748b", fontSize: 14, marginBottom: 24 },
  input: { width: "100%", padding: "12px 14px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 15, marginBottom: 14, boxSizing: "border-box", outline: "none" },
  btnPrimary: { width: "100%", padding: "12px", background: "#ff5421", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer" },
  errorBox: { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14, textAlign: "left" },
  dashPage: { minHeight: "100vh", background: "#f8fafc", fontFamily: "'Segoe UI', system-ui, sans-serif" },
  topbar: { background: "#ff5421", color: "#fff", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 2px 8px rgba(79,70,229,0.2)" },
  topbarLogo: { fontSize: 18, fontWeight: 700 },
  btnLogout: { background: "rgba(255,255,255,0.15)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", cursor: "pointer", fontSize: 13 },
  dashBody: { maxWidth: 1320, margin: "0 auto", padding: "24px 20px" },
  successBanner: { background: "#d1fae5", border: "1px solid #6ee7b7", color: "#065f46", borderRadius: 10, padding: "12px 16px", fontSize: 14, marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  closeBanner: { background: "none", border: "none", color: "#065f46", fontSize: 18, cursor: "pointer", fontWeight: 700 },
  tabRow: { display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" },
  tabBtn: { padding: "9px 20px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 14, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 },
  tabBtnActive: { background: "#ff5421", color: "#fff", border: "1.5px solid #ff5421" },
  // 📭 NEW — tab par count badge
  tabBadge: { background: "#dc2626", color: "#fff", borderRadius: 10, padding: "1px 8px", fontSize: 11, fontWeight: 700, lineHeight: "16px" },
  tabBadgeActive: { background: "#fff", color: "#ff5421" },
  filterRow: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  reqSearchBox: { display: "flex", gap: 8, marginBottom: 16, background: "#fff", padding: "12px 14px", borderRadius: 10, border: "1px solid #e2e8f0" },
  filterBtn: { padding: "7px 16px", borderRadius: 20, border: "1.5px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 13, cursor: "pointer" },
  filterBtnActive: { background: "#ff5421", color: "#fff", border: "1.5px solid #ff5421", fontWeight: 600 },
  refreshBtn: { padding: "7px 16px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 13, cursor: "pointer", marginLeft: "auto" },
  tableWrapper: { background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: { background: "#f8fafc", color: "#475569", fontWeight: 600, padding: "12px 16px", textAlign: "left", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" },
  td: { padding: "12px 16px", color: "#1e293b", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
  tr: { transition: "background 0.15s" },
  actionRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", minWidth: 210 },
  btnApprove: { background: "#d1fae5", color: "#065f46", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnReject: { background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnEdit: { background: "#e0e7ff", color: "#4338ca", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnResend: { background: "#ffedd5", color: "#9a3412", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnView: { background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  linkBadge: { padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" },
  checkRow: { display: "flex", gap: 10, alignItems: "flex-start", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "12px 14px", marginTop: 14, fontSize: 13, color: "#7c2d12", cursor: "pointer", lineHeight: 1.5 },
  centered: { textAlign: "center", padding: 40, color: "#64748b", fontSize: 16 },
  emptyState: { textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 15 },
  reattemptBadge: { background: "#fef9c3", color: "#854d0e", padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 },
  modalCard: { background: "#fff", borderRadius: 14, padding: "28px 28px 24px", width: 440, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "#1e1b4b", margin: "0 0 18px", paddingBottom: 12, borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  modalLabel: { fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, marginTop: 12, display: "block" },
  btnSave: { flex: 1, padding: "11px", background: "#ff5421", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  btnCancel: { flex: 1, padding: "11px", background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  webinarTopRow: { display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" },
  uploadBox: { background: "#fff", borderRadius: 12, padding: "18px 20px", border: "1.5px dashed #c7d2fe", flex: 1, minWidth: 260 },
  searchBox: { background: "#fff", borderRadius: 12, padding: "18px 20px", border: "1px solid #e2e8f0", flex: 1, minWidth: 260 },
  uploadTitle: { fontSize: 15, fontWeight: 700, color: "#1e1b4b", marginBottom: 6 },
  uploadHint: { fontSize: 12, color: "#64748b", marginBottom: 10 },
  uploadBtn: { display: "inline-block", background: "#ff5421", color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnSearch: { background: "#ff5421", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" },
  statsRow: { display: "flex", gap: 12, marginBottom: 16 },
  statCard: { background: "#fff", borderRadius: 10, padding: "14px 20px", border: "1px solid #e2e8f0", minWidth: 120 },
  statNum: { fontSize: 28, fontWeight: 800, color: "#ff5421" },
  statLabel: { fontSize: 12, color: "#64748b", fontWeight: 600, marginTop: 2 },
  verifyBadge: { background: "#f0fdf4", color: "#15803d", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: "monospace" },
  pagination: { display: "flex", alignItems: "center", gap: 6, marginTop: 16, flexWrap: "wrap" },
  pageBtn: { padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  pageBtnActive: { background: "#ff5421", color: "#fff", border: "1.5px solid #ff5421" },
  pageBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  pageDots: { color: "#94a3b8", fontSize: 14, padding: "0 4px" },
  pageInfo: { marginLeft: "auto", fontSize: 12, color: "#64748b", fontWeight: 600 },
};